if (!customElements.get('media-gallery')) {
  class MediaGallery extends HTMLElement {
    constructor() {
      super();

      if (Shopify.designMode) {
        setTimeout(() => this.init(), 200);
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('on:debounced-resize', this.resizeHandler);

      if (this.resizeInitHandler) {
        window.removeEventListener('on:debounced-resize', this.resizeInitHandler);
      }
    }

    init() {
      this.section = this.closest('.js-product');
      this.mediaGroupingEnabled = this.hasAttribute('data-media-grouping-enabled')
        && this.getMediaGroupData();
      this.stackedScroll = this.dataset.stackedScroll;
      this.stackedUnderline = this.dataset.stackedUnderline === 'true' && !this.mediaGroupingEnabled;
      this.isFeatured = this.dataset.isFeatured === 'true';
      this.viewer = this.querySelector('.media-viewer');
      this.thumbs = this.querySelector('.media-thumbs');
      this.thumbsItems = this.querySelectorAll('.media-thumbs__item');
      this.controls = this.querySelector('.media-ctrl');
      this.prevBtn = this.querySelector('.media-ctrl__btn[name="prev"]');
      this.nextBtn = this.querySelector('.media-ctrl__btn[name="next"]');
      this.counterCurrent = this.querySelector('.media-ctrl__current-item');
      this.counterTotal = this.querySelector('.media-ctrl__total-items');
      this.liveRegion = this.querySelector('.media-gallery__status');
      this.zoomLinks = this.querySelectorAll('.js-zoom-link');
      this.loadingSpinner = this.querySelector('.loading-spinner');
      this.xrButton = this.querySelector('.media-xr-button');

      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        this.resizeInitHandler = this.resizeInitHandler || this.initGallery.bind(this);
        window.addEventListener('on:debounced-resize', this.resizeInitHandler);
        this.setVisibleItems();
        this.previousMediaItem = this.querySelector('.media-viewer__item.is-current-variant');
        setTimeout(() => this.customSetActiveMedia(this.previousMediaItem, this.stackedScroll === 'always'), 200);
      } else {
        this.initGallery();
      }

      this.initImagePopupLinks();

      if (this.section) {
        this.section.addEventListener('on:variant:change', this.onVariantChange.bind(this));
      }
    }

    initImagePopupLinks() {
      if (!this.zoomLinks || !this.zoomLinks.length) return;

      this.zoomLinks.forEach((el, index) => {
        el.classList.remove('media--zoom');
        el.classList.remove('media--zoom-not-loaded');
        el.classList.remove('pointer-events-none');

        el.addEventListener('mousemove', function (evt) {
          evt.stopPropagation();
        });

        el.addEventListener('click', (evt) => {
          evt.preventDefault();
          this.setVisibleItems();

          const visibleLinks = Array.from(this.visibleItems)
            .map((item) => item.querySelector('.js-zoom-link'))
            .filter(Boolean);

          const clickedIndex = visibleLinks.indexOf(el);
          this.openLightbox(clickedIndex >= 0 ? clickedIndex : index);
        });
      });
    }

    openLightbox(index) {
      this.setVisibleItems();

      const lightbox = document.getElementById('customProductLightbox');
      const image = document.getElementById('customLightboxImage');
      const current = document.getElementById('customLightboxCurrent');
      const total = document.getElementById('customLightboxTotal');

      if (!lightbox || !image || !this.visibleItems.length) return;

      this.lightboxIndex = index;

      const activeItem = this.visibleItems[this.lightboxIndex];
      const imageLink = activeItem ? activeItem.querySelector('.js-zoom-link') : null;

      if (!imageLink) return;

      image.src = imageLink.getAttribute('href');
      image.alt = imageLink.querySelector('img')?.alt || 'Product image';

      if (current) current.textContent = this.lightboxIndex + 1;
      if (total) total.textContent = this.visibleItems.length;

      this.renderLightboxTrack(lightbox);

      lightbox.classList.add('is-open');
      if (!theme.mediaMatches.md) {
        const activeTrackItem = lightbox.querySelector(`.custom-lightbox-track-item[data-index="${this.lightboxIndex}"]`);
        if (activeTrackItem) activeTrackItem.scrollIntoView({ block: 'start' });
      }
      document.body.style.overflow = 'hidden';
    }

    closeLightbox() {
      const lightbox = document.getElementById('customProductLightbox');
      const image = document.getElementById('customLightboxImage');

      if (lightbox) lightbox.classList.remove('is-open');
      if (image) image.src = '';

      document.body.style.overflow = '';
    }

    renderLightboxTrack(lightbox) {
      let track = lightbox.querySelector('.custom-lightbox-track');

      if (!track) {
        track = document.createElement('div');
        track.className = 'custom-lightbox-track';
        lightbox.appendChild(track);

        this.lightboxScrollHandler = this.lightboxScrollHandler || this.handleLightboxScroll.bind(this);
        lightbox.addEventListener('scroll', this.lightboxScrollHandler);
      }

      track.innerHTML = '';
      this.visibleItems.forEach((item, index) => {
        const imageLink = item.querySelector('.js-zoom-link');
        if (!imageLink) return;

        const trackItem = document.createElement('div');
        trackItem.className = 'custom-lightbox-track-item';
        trackItem.dataset.index = index;

        const trackImage = document.createElement('img');
        trackImage.src = imageLink.getAttribute('href');
        trackImage.alt = imageLink.querySelector('img')?.alt || 'Product image';
        trackImage.loading = index === this.lightboxIndex ? 'eager' : 'lazy';

        trackItem.appendChild(trackImage);
        track.appendChild(trackItem);
      });
    }

    handleLightboxScroll() {
      if (theme.mediaMatches.md) return;

      const lightbox = document.getElementById('customProductLightbox');
      const current = document.getElementById('customLightboxCurrent');
      const track = lightbox ? lightbox.querySelector('.custom-lightbox-track') : null;
      if (!lightbox || !track || !lightbox.classList.contains('is-open')) return;

      const scrollTop = lightbox.scrollTop;
      const viewportCenter = scrollTop + (lightbox.clientHeight / 2);
      const items = Array.from(track.children);
      if (!items.length) return;

      const nextIndex = items.reduce((closestIndex, item, index) => {
        const itemCenter = item.offsetTop + (item.offsetHeight / 2);
        const closestItem = items[closestIndex];
        const closestCenter = closestItem.offsetTop + (closestItem.offsetHeight / 2);
        return Math.abs(itemCenter - viewportCenter) < Math.abs(closestCenter - viewportCenter)
          ? index
          : closestIndex;
      }, 0);

      if (nextIndex === this.lightboxIndex) return;

      this.lightboxIndex = nextIndex;
      if (current) current.textContent = this.lightboxIndex + 1;
    }

    nextLightbox() {
      if (!this.visibleItems || !this.visibleItems.length) return;

      this.lightboxIndex = (this.lightboxIndex + 1) % this.visibleItems.length;
      this.openLightbox(this.lightboxIndex);
    }

    prevLightbox() {
      if (!this.visibleItems || !this.visibleItems.length) return;

      this.lightboxIndex =
        (this.lightboxIndex - 1 + this.visibleItems.length) % this.visibleItems.length;

      this.openLightbox(this.lightboxIndex);
    }

    onVariantChange(evt) {
      if (this.mediaGroupingEnabled) {
        this.setActiveMediaGroup(this.getMediaGroupFromOptionSelectors());
      }

      if (evt.detail.variant && evt.detail.variant.featured_media) {
        const variantMedia = this.viewer.querySelector(
          `[data-media-id="${evt.detail.variant.featured_media.id}"]`
        );
        this.customSetActiveMedia(variantMedia, true);
      }
    }

    getMediaGroupFromOptionSelectors() {
      const optionSelectors = this.section.querySelectorAll('.option-selector');

      if (optionSelectors.length > this.getMediaGroupData().groupOptionIndex) {
        const selector = optionSelectors[this.getMediaGroupData().groupOptionIndex];

        if (selector.dataset.selectorType === 'dropdown') {
          return selector.querySelector('.custom-select__btn').textContent.trim();
        }

        return selector.querySelector('input:checked').value;
      }

      return null;
    }

    getMediaGroupData() {
      if (typeof this.variantMediaData === 'undefined') {
        const dataEl = this.querySelector('.js-data-variant-media');
        this.variantMediaData = dataEl ? JSON.parse(dataEl.textContent) : false;
      }

      return this.variantMediaData;
    }

    getMediaGroupMap() {
      if (!this.mediaGroupMap) {
        this.mediaGroupMap = {
          groups: {}
        };

        const variantMediaData = this.getMediaGroupData();
        let currentMediaOptionName = false;

        this.viewerItems = this.querySelectorAll('.media-viewer__item');

        this.viewerItems.forEach((item) => {
          for (let i = 0; i < variantMediaData.variantMedia.length; i += 1) {
            if (parseInt(item.dataset.mediaId, 10) === variantMediaData.variantMedia[i].mediaId) {
              if (currentMediaOptionName !== variantMediaData.variantMedia[i].option) {
                currentMediaOptionName = variantMediaData.variantMedia[i].option;
              }
            }
          }

          if (currentMediaOptionName) {
            if (!this.mediaGroupMap.groups[currentMediaOptionName]) {
              this.mediaGroupMap.groups[currentMediaOptionName] = {
                name: currentMediaOptionName,
                items: []
              };
            }

            const groupItem = { main: item };

            if (this.thumbs) {
              groupItem.thumb = this.thumbs.querySelector(
                `[data-media-id="${item.dataset.mediaId}"].media-thumbs__item`
              );
            }

            this.mediaGroupMap.groups[currentMediaOptionName].items.push(groupItem);
          }
        });

        this.mediaGroupMap.groupFromItem = (item) => {
          const groups = Object.keys(this.mediaGroupMap.groups);

          for (let i = 0; i < groups.length; i += 1) {
            const group = groups[i];

            for (let j = 0; j < this.mediaGroupMap.groups[group].items.length; j += 1) {
              if (this.mediaGroupMap.groups[group].items[j].main === item) {
                return this.mediaGroupMap.groups[group];
              }
            }
          }

          return this.mediaGroupMap.groups[Object.keys(this.mediaGroupMap.groups)[0]];
        };
      }

      return this.mediaGroupMap;
    }

    setActiveMediaGroup(groupName) {
      const mediaGroupMap = this.getMediaGroupMap();
      const selectedGroup = groupName
        ? mediaGroupMap.groups[groupName]
        : mediaGroupMap.groupFromItem(this.currentItem);

      if (selectedGroup) {
        if (this.currentGroup !== selectedGroup) {
          this.currentGroup = selectedGroup;

          this.viewerItems.forEach((item) => {
            item.style.display = 'none';
            item.classList.remove('media-viewer__item--single');
          });

          this.thumbsItems.forEach((item) => {
            item.style.display = 'none';
          });

          let currentItemIsVisible = false;

          selectedGroup.items.forEach((item) => {
            item.main.style.display = '';

            if (item.thumb) {
              item.thumb.style.display = '';
            }

            if (item.main === this.currentItem) {
              currentItemIsVisible = true;
            }
          });

          this.setVisibleItems();

          if (!currentItemIsVisible) {
            this.customSetActiveMedia(selectedGroup.items[0].main, true);
          }

          if (selectedGroup.items.length === 1) {
            selectedGroup.items[0].main.classList.add('media-viewer__item--single');
          }
        }
      } else {
        this.viewerItems.forEach((item) => {
          item.style.display = '';
        });

        this.thumbsItems.forEach((item) => {
          item.style.display = '';
        });
      }
    }

    initGallery() {
      this.setVisibleItems();

      if (this.visibleItems.length <= 1) return;

      this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      this.currentIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);
      this.currentItem = this.visibleItems[this.currentIndex];
      this.addListeners();

      if (this.thumbs && this.currentItem) {
        this.currentThumb = this.thumbs.querySelector(
          `[data-media-id="${this.currentItem.dataset.mediaId}"]`
        );
      }

      if (!this.isFeatured && document.hasFocus()) {
        this.viewer.querySelectorAll('.product-image[loading="lazy"]').forEach((img, index) => {
          setTimeout(() => {
            img.loading = 'eager';
          }, 500 * (index + 1));
        });
      }

      const currentItem = this.querySelector('.media-viewer__item.is-current-variant');
      this.customSetActiveMedia(currentItem, true);
    }

    addListeners() {
      this.viewer.addEventListener('scroll', this.handleScroll.bind(this));

      if (this.controls) {
        this.controls.addEventListener('click', this.handleNavClick.bind(this));
      }

      if (this.thumbs) {
        this.thumbs.addEventListener('click', this.handleThumbClick.bind(this));
      }

      this.resizeHandler = this.resizeHandler || this.handleResize.bind(this);
      window.addEventListener('on:debounced-resize', this.resizeHandler);
    }

    handleScroll() {
      const newIndex = Math.round(this.viewer.scrollLeft / this.viewerItemOffset);

      if (newIndex !== this.currentIndex) {
        const viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;

        if (viewerItemOffset === this.viewerItemOffset) {
          this.customSetActiveMedia(this.visibleItems[newIndex], false);
        }
      }
    }

    handleNavClick(evt) {
      if (!evt.target.matches('.media-ctrl__btn')) return;

      const itemToShow = evt.target === this.nextBtn
        ? this.visibleItems[this.currentIndex + 1]
        : this.visibleItems[this.currentIndex - 1];

      this.viewer.scrollTo({ left: itemToShow.offsetLeft, behavior: 'smooth' });
    }

    handleThumbClick(evt) {
      const thumb = evt.target.closest('[data-media-id]');
      if (!thumb) return;

      const itemToShow = this.querySelector(`[data-media-id="${thumb.dataset.mediaId}"]`);
      this.customSetActiveMedia(itemToShow);

      MediaGallery.playActiveMedia(itemToShow);
    }

    handleResize() {
      this.setVisibleItems();

      if (this.visibleItems.length > 1) {
        this.viewerItemOffset = this.visibleItems[1].offsetLeft - this.visibleItems[0].offsetLeft;
      }

      if (this.thumbs && this.currentThumb) {
        this.checkThumbVisibilty(this.currentThumb);
      }
    }

    setActiveMedia() {}

    customSetActiveMedia(mediaItem, scrollToItem = true) {
      if (!mediaItem || mediaItem === this.currentItem) return;

      window.pauseAllMedia(this);

      this.currentItem = mediaItem;
      this.currentIndex = this.visibleItems.indexOf(this.currentItem);

      if (this.dataset.layout === 'stacked' && theme.mediaMatches.md) {
        if (this.stackedUnderline) {
          if (this.previousMediaItem) this.previousMediaItem.classList.remove('is-active');

          mediaItem.classList.add('is-active');
          this.previousMediaItem = mediaItem;
        }

        if (this.stackedScroll !== 'never') {
          const y = mediaItem.getBoundingClientRect().top
            + document.documentElement.scrollTop - 150;

          if (Math.abs(y - document.documentElement.scrollTop) > 300) {
            window.scrollTo({
              top: y < 100 ? 0 : y,
              behavior: 'smooth'
            });
          }
        }

        return;
      }

      if (scrollToItem) {
        this.viewer.scrollTo({ left: this.currentItem.offsetLeft });
      }

      if (this.thumbs) {
        this.setActiveThumb();
      }

      if (this.controls) {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }

        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex === this.visibleItems.length - 1;
        }

        if (this.counterCurrent) {
          this.counterCurrent.textContent = this.currentIndex + 1;
        }
      }

      this.announceLiveRegion(this.currentItem, this.currentIndex + 1);

      if (this.xrButton && mediaItem.dataset.mediaType === 'model') {
        this.xrButton.dataset.shopifyModel3dId = mediaItem.dataset.mediaId;
      }
    }

    setActiveThumb() {
      this.currentThumb = this.thumbs.querySelector(
        `[data-media-id="${this.currentItem.dataset.mediaId}"]`
      );

      if (!this.currentThumb) return;

      const btn = this.currentThumb.querySelector('button');

      this.thumbs.querySelectorAll('.media-thumbs__btn').forEach((el) => {
        el.classList.remove('is-active');
        el.removeAttribute('aria-current');
      });

      btn.classList.add('is-active');
      btn.setAttribute('aria-current', 'true');
      this.checkThumbVisibilty(this.currentThumb);
    }

    setVisibleItems() {
      this.viewerItems = this.querySelectorAll('.media-viewer__item');

      this.visibleItems = Array.from(this.viewerItems).filter((el) => {
        return el.clientWidth > 0 && el.style.display !== 'none';
      });

      if (this.counterTotal) {
        this.counterTotal.textContent = this.visibleItems.length;
      }
    }

    checkThumbVisibilty(thumb) {
      const scrollPos = this.thumbs.scrollLeft;
      const lastVisibleThumbOffset = this.thumbs.clientWidth + scrollPos;
      const thumbOffset = thumb.offsetLeft;

      if (thumbOffset + thumb.clientWidth > lastVisibleThumbOffset || thumbOffset < scrollPos) {
        this.thumbs.scrollTo({ left: thumbOffset, behavior: 'smooth' });
      }
    }

    announceLiveRegion(mediaItem, index) {
      const image = mediaItem.querySelector('.media-viewer img');

      if (!image) return;

      this.liveRegion.setAttribute('aria-hidden', 'false');
      this.liveRegion.innerHTML = theme.strings.imageAvailable.replace('[index]', index);

      setTimeout(() => {
        this.liveRegion.setAttribute('aria-hidden', 'true');
      }, 2000);
    }

    static playActiveMedia(mediaItem) {
      window.pauseAllMedia();

      const deferredMedia = mediaItem.querySelector('deferred-media');

      if (deferredMedia) deferredMedia.loadContent();
    }
  }

  customElements.define('media-gallery', MediaGallery);
}

document.addEventListener('DOMContentLoaded', function () {
  const lightbox = document.getElementById('customProductLightbox');

  if (!lightbox) return;

  document.querySelector('.custom-lightbox-close')?.addEventListener('click', function () {
    document.querySelector('media-gallery')?.closeLightbox();
  });

  document.querySelector('.custom-lightbox-next')?.addEventListener('click', function () {
    document.querySelector('media-gallery')?.nextLightbox();
  });

  document.querySelector('.custom-lightbox-prev')?.addEventListener('click', function () {
    document.querySelector('media-gallery')?.prevLightbox();
  });

  lightbox.addEventListener('click', function (evt) {
    if (evt.target === lightbox) {
      document.querySelector('media-gallery')?.closeLightbox();
    }
  });

  document.addEventListener('keydown', function (evt) {
    if (!lightbox.classList.contains('is-open')) return;

    if (evt.key === 'Escape') {
      document.querySelector('media-gallery')?.closeLightbox();
    }

    if (evt.key === 'ArrowRight') {
      document.querySelector('media-gallery')?.nextLightbox();
    }

    if (evt.key === 'ArrowLeft') {
      document.querySelector('media-gallery')?.prevLightbox();
    }
  });
});
